export type WorkerStatus = "PENDING" | "APPROVED" | "SUSPENDED";

export type LegalStatus = "CITIZEN" | "PERMANENT_RESIDENT" | "OPEN_WORK_PERMIT";

export type ExperienceLevel = "ENTRY" | "INTERMEDIATE" | "EXPERIENCED" | "EXPERT";

export const LEGAL_STATUS_LABELS: Record<LegalStatus, string> = {
  CITIZEN: "Canadian citizen",
  PERMANENT_RESIDENT: "Permanent resident",
  OPEN_WORK_PERMIT: "Valid open work permit",
};

export const EXPERIENCE_LEVEL_LABELS: Record<ExperienceLevel, string> = {
  ENTRY: "Entry level",
  INTERMEDIATE: "Intermediate",
  EXPERIENCED: "Experienced",
  EXPERT: "Expert",
};

export const EXPERIENCE_LEVEL_DESCRIPTIONS: Record<ExperienceLevel, string> = {
  ENTRY: "No experience required",
  INTERMEDIATE: "Some experience required",
  EXPERIENCED: "Proficient",
  EXPERT: "Professional level — high-security or VIP settings",
};

export interface Certification {
  id: string;
  url: string;
  name: string;
  /**
   * One of the four allow-listed categories from
   * `shared/lib/certCategories.ts`. Required on new uploads; nullable on
   * legacy rows that pre-date the field.
   */
  category: string | null;
  expires_on: string | null; // YYYY-MM-DD
  created_at: string;
}

export interface WorkerProfile {
  id: string;
  user_id: string;
  name: string;
  photo_url: string | null;
  certifications: Certification[];
  city: string | null;
  bio: string | null;
  legal_status: LegalStatus | null;
  experience_level: ExperienceLevel | null;
  onboarding_completed_at: string | null;
  sin_provided: boolean;
  work_permit_expires_on: string | null; // YYYY-MM-DD
  /** Decimal CAD/hour. Acts as a hard floor on the worker's shift feed. */
  min_hourly_rate: string | null;
  eligibility_document_url: string | null;
  eligibility_document_uploaded_at: string | null;
  status: WorkerStatus;
  rating_avg: number | null;
  shift_count: number;
}

export interface UpdateWorkerProfilePayload {
  name?: string;
  phone?: string;
  city?: string;
  bio?: string;
  legal_status?: LegalStatus;
  experience_level?: ExperienceLevel;
  /** 9-digit Canadian SIN. Server normalizes, encrypts, and stores —
   * the value is never echoed back. The form should send it once on
   * onboarding and rely on `sin_provided` thereafter. */
  sin?: string;
  work_permit_expires_on?: string | null; // YYYY-MM-DD
  /** Decimal as string — the API ships Decimal serialized; we keep it as-is. */
  min_hourly_rate?: string;
}
